// eslint-disable-next-line
///<reference path="../../../../../../../jsdoc-types.js" />

import Vue from 'vue';
import Axios from 'axios';
import pMinDelay from 'p-min-delay';
import {
  parseRelativeUrl,
  urlWithoutParams,
} from '../../../../common/utils/url';
import loadColorVariation from '../../../utils/loadColorVariation';
import {
  getSliderBannerKey,
  isSliderBannerEnriched,
} from '../../../utils/sliderBannerProducts';
import {
  getAppliedSortingOption,
  hasAnySortingOptionApplied,
  isRefinementValueForPrice,
} from '../../../utils/productSearch';
import { comparePageNumber, haveSameCategory } from './compareProductSearch';
import createCategorySeparator from './createCategorySeparator';
import { createProductSearchPrefetcher } from '../../../utils/createProductSearchPrefetcher';
import getFrontConfig from '../../../../common/getFrontConfig';

const FRONT_CONFIG = getFrontConfig();

export const types = {
  applyFilters: 'productList/applyFilters',
  hide: 'productList/hide',
  init: 'productList/init',
  loadColorVariation: 'productList/loadColorVariation',
  loadSliderBannerProducts: 'productList/loadSliderBannerProducts',
  loadFilteredListFromUrl: 'productList/loadFilteredListFromUrl',
  loadNewListFromUrl: 'productList/loadNewListFromUrl',
  loadPageFromUrl: 'productList/loadPageFromUrl',
  resetList: 'productList/resetList',
  resetUiFilters: 'productList/resetUiFilters',
  setVisibleCategory: 'productList/setVisibleCategory',
  setViewAllCategoryId: 'productList/setViewAllCategoryId',
  selectFilter: 'productList/selectFilter',
  deselectFilter: 'productList/deselectFilter',
  deselectPriceFilter: 'productList/deselectPriceFilter',
  selectSortingOption: 'productList/selectSortingOption',
  setColumnMode: 'productList/setColumnMode',
  show: 'productList/show',
};

export const getters = {
  products: 'productList/products',
  numAppliedFilters: 'productList/numAppliedFilters',
  uiNumAppliedFilters: 'productList/uiNumAppliedFilters',
  uiNumSelectedFilters: 'productList/uiNumSelectedFilters',
  visibleCategory: 'productList/visibleCategory',
  currentCategory: 'productList/currentCategory',
  viewAllCategoryId: 'productList/viewAllCategoryId',
};

let productSearchPrefetcher;

// Keys of slider banners whose products are currently being lazy-loaded, to
// avoid firing duplicate requests while a fetch is in flight (the dispatch can
// be triggered repeatedly on mount/list-change/resize).
const pendingSliderBanners = new Set();

/**
 * Find the canonical state reference of a slider banner (the one in
 * state.products) so mutations operate on tracked state.
 * @param {ProductListState} state productList state
 * @param {Object} banner banner object to locate
 * @returns {Object|null} the state banner reference, or null if not found
 */
const findSliderBannerInState = (state, banner) => {
  let found = null;
  state.products.some((category) =>
    category.items.some((item) => {
      if (item.product?.banner !== banner) return false;
      found = item.product.banner;
      return true;
    })
  );
  return found;
};

const mutateCategory = ({ commit, state }, productSearch) => {
  let mutation = null;

  if (haveSameCategory(state.productSearch, productSearch)) {
    const pageNumberComparison = comparePageNumber(
      state.productSearch,
      productSearch
    );

    if (pageNumberComparison === 1) {
      mutation = 'appendCategoryPage';
    } else if (pageNumberComparison === -1) {
      mutation = 'prependCategoryPage';
    }
  } else {
    mutation = 'appendAnotherCategoryPage';
  }

  if (mutation) {
    commit(mutation, productSearch);
  }

  return productSearch;
};

const stateEmptyList = {
  filtersUrl: null,
  loadedUrls: [],
  products: [],
  productSearch: null,
  visibleCategory: null,
};

const filtersWithoutCategory = (filters) =>
  filters?.filter((filter) => filter.type !== 'category');

/**
 * Remove any applied price filter from a state
 * @param {ProductListState} state - state of the module
 * @returns {undefined}
 */
function removeAnyPriceFilterFromState(state) {
  state.ui.selectedFilters = state.ui.selectedFilters.filter(
    (filter) => !isRefinementValueForPrice(filter)
  );
}

/**
 * @typedef ProductListState
 * @type {Object}
 * @property {Object} ui ui related flags
 * @property {string} [ui.columnMode="default"] Column mode used in grid (normal or compressed columns)
 * @property {boolean} [ui.hidden=true] Is PLP hidden
 * @property {boolean} ui.moreCategoriesAppended PLP have shown products from any following category
 * @property {boolean} ui.moreProductsAppended PLP have shown products from any following page
 * @property {ProductSearchRefinement[]} ui.initialRefinements - original refinements from not filtered productSearch
 * @property {SortingOption[]} ui.initialSortingOptions original sortingOptions from not sorted prodcutSearch
 * @property {ProductSearchRefinementValue[]} ui.appliedFilters applied refinement values in ui
 * @property {SortingOption} ui.appliedSortingOption applied sortingOption in ui
 * @property {ProductSearchRefinementValue[]} ui.selectedFilters selected refinement values in ui
 * @property {SortingOption} ui.selectedSortingOption selected sortingOption in ui
 * @property {boolean} loading Is there any pending request
 * @property {Object} caches
 * @property {Object.<string, Object>} caches.categoriesByUrl loaded category objects by url
 * @property {string} filtersUrl last filters URL used to filter items in PLP. Useful for not refetch
 * @property {string[]} [loadedUrls=[]] URLs used to add items to PLP. Useful for not refetch
 * current data when going back in history
 * @property {{category:object,items:object[]}[]} products - Tile model arrays grouped by category
 * @property {ProductSearch} productSearch - Last search related data (count, pageNumber, pageSize,
 * products of the page, selected filters...)
 * @property {object} visibleCategory - Current category data (id, name, pageDescription, pageKeywords,
 * pageTitle...)
 * @property {?string} viewAllCategoryId - Id of the category being displayed as
 * its own view all, null while displaying the category itself
 */

/** @type ProductListState  */
const stateInitial = {
  ui: {
    columnMode: FRONT_CONFIG.vue.TheProductList.columnMode.default,
    hidden: true,
    moreProductsAppended: false,
    moreProductsPrepended: false,
    moreCategoriesAppended: false,
    initialRefinements: [],
    initialSortingOptions: [],
    appliedFilters: [],
    appliedSortingOption: null,
    selectedFilters: [],
    selectedSortingOption: null,
  },
  caches: {
    categoriesByUrl: {},
  },
  loading: false,
  // Id of the category being displayed as its own view all. Kept out of
  // stateEmptyList on purpose so a list reset does not drop the view.
  viewAllCategoryId: null,
  ...stateEmptyList,
};

export default {
  namespaced: true,
  state: stateInitial,
  mutations: {
    /**
     * Show the product list
     * @param {ProductListState} state - state of the module
     */
    show(state) {
      state.ui.hidden = false;
    },
    /**
     * Hide the product list
     * @param {ProductListState} state - state of the module
     */
    hide(state) {
      state.ui.hidden = true;
    },
    /**
     * Record the URL the list is currently filtered by, to avoid refetching it
     * @param {ProductListState} state - state of the module
     * @param {string} url - URL the filters were applied with
     */
    filtered(state, url) {
      state.filtersUrl = url;
    },
    /**
     * Mark a URL as already loaded and end the loading state. The first loaded
     * URL also becomes the filters one when none has been set yet.
     * @param {ProductListState} state - state of the module
     * @param {string} url - URL whose data has just been loaded
     */
    loaded(state, url) {
      state.loading = false;
      state.loadedUrls = [...state.loadedUrls, url];
      state.filtersUrl = state.filtersUrl || url;
    },
    /**
     * Start the loading state, optionally emptying the list first
     * @param {ProductListState} state - state of the module
     * @param {boolean} [resetList=false] - true to empty the list before loading
     */
    loading(state, resetList = false) {
      state.loading = true;
      if (resetList) {
        Object.assign(state, stateEmptyList);
      }
    },
    /**
     * Enrich a slider banner's `productSlider` in place with its full
     * (lazy-loaded) tile data, merging by id and preserving the lightweight
     * fields (imageUrl, title…) so mobile keeps working. Reassigns the array so
     * the grid (which paints from banner.productSlider) reacts.
     * @param {ProductListState} state productList state
     * @param {{banner: Object, products: Object[]}} payload banner and its full tiles
     */
    enrichSliderBannerProducts(state, { banner, products }) {
      // Operate on the state reference (a local var, not the banner param) so
      // the grid, which paints from products[..].product.banner.productSlider,
      // reacts to the change.
      const stateBanner = findSliderBannerInState(state, banner);
      if (!stateBanner) return;
      const fullById = Object.fromEntries(
        products.map((product) => [product.id, product])
      );
      stateBanner.productSlider = stateBanner.productSlider.map(
        (sliderProduct) =>
          fullById[sliderProduct.id]
            ? { ...sliderProduct, ...fullById[sliderProduct.id] }
            : sliderProduct
      );
      Vue.set(stateBanner, 'sliderProductsUnavailable', false);
    },
    /**
     * Mark a slider banner's lazy-load as resolved with no usable products
     * (network error or all products offline) so desktop hides it instead of
     * showing permanent skeletons. Leaves the lightweight productSlider intact
     * for mobile.
     * @param {ProductListState} state productList state
     * @param {{banner: Object}} payload the banner that failed to load
     */
    failSliderBannerProducts(state, { banner }) {
      const stateBanner = findSliderBannerInState(state, banner);
      if (!stateBanner) return;
      Vue.set(stateBanner, 'sliderProductsUnavailable', true);
    },
    /**
     * Append the first page of a different category to the end of the list,
     * keeping it as its own group so a separator can be drawn between both
     * @param {ProductListState} state - state of the module
     * @param {ProductSearch} productSearch - search results of the new category
     */
    appendAnotherCategoryPage(state, productSearch) {
      state.ui.moreCategoriesAppended = true;
      state.ui.moreProductsAppended = true;
      state.products = [
        ...state.products,
        {
          category: productSearch.category,
          items: productSearch.products,
        },
      ];
      state.productSearch = productSearch;
      state.caches.categoriesByUrl[productSearch.category.url] =
        productSearch.category;
    },
    /**
     * Append a further page of the last category already in the list
     * @param {ProductListState} state - state of the module
     * @param {ProductSearch} productSearch - search results of the page
     */
    appendCategoryPage(state, productSearch) {
      state.ui.moreProductsAppended = true;
      const currentCategory = state.products[state.products.length - 1];
      currentCategory.items = [
        ...currentCategory.items,
        ...productSearch.products,
      ];
      state.productSearch = productSearch;
    },
    /**
     * Prepend a previous page to the first category in the list, used when
     * scrolling upwards into pages that were skipped
     * @param {ProductListState} state - state of the module
     * @param {ProductSearch} productSearch - search results of the page
     */
    prependCategoryPage(state, productSearch) {
      state.ui.moreProductsPrepended = true;
      const currentCategory = state.products[0];
      currentCategory.items = [
        ...productSearch.products,
        ...currentCategory.items,
      ];
      state.productSearch = productSearch;
    },
    /**
     * Set selected filters as applied filters in ui
     * @param {ProductListState} state - state of the module
     */
    applyFilters(state) {
      state.ui.appliedFilters = state.ui.selectedFilters;
      state.ui.appliedSortingOption = state.ui.selectedSortingOption;
    },
    /**
     * Set sorting and filtering to default value
     * @param {ProductListState} state - state of the module
     * @param {ProductSearch} [productSearch=null] - product search used as initial state of the filters
     */
    resetFilters(state, productSearch = null) {
      state.ui.initialRefinements = [];
      state.ui.initialSortingOptions = null;
      state.ui.appliedFilters = [];
      state.ui.appliedSortingOption = null;
      state.ui.selectedFilters = [];
      state.ui.selectedSortingOption = null;

      if (productSearch) {
        state.ui.initialRefinements = productSearch.refinements;
        state.ui.initialSortingOptions = productSearch.productSort.options;
        const sortingOption = getAppliedSortingOption(productSearch);
        if (sortingOption !== null) {
          state.ui.appliedSortingOption = sortingOption;
          state.ui.selectedSortingOption = sortingOption;
        }
        state.ui.appliedFilters = filtersWithoutCategory(
          productSearch.selectedFilters
        );
        state.ui.selectedFilters = filtersWithoutCategory(
          productSearch.selectedFilters
        );
      }
    },
    /**
     * Reset selected filters/sorting options in ui
     * @param {ProductListState} state - state of the module
     */
    resetUiFilters(state) {
      state.ui.appliedFilters = [];
      state.ui.appliedSortingOption = null;
      state.ui.selectedFilters = [];
      state.ui.selectedSortingOption = null;
    },
    /**
     * Replace the whole list with the results of a single category, caching it
     * by url and making it the visible one
     * @param {ProductListState} state - state of the module
     * @param {ProductSearch} productSearch - search results to start over with
     */
    resetStateList: (state, productSearch) => {
      state.ui.moreCategoriesAppended = false;
      state.ui.moreProductsAppended = false;
      state.visibleCategory = productSearch.category;
      state.products = [
        {
          category: productSearch.category,
          items: productSearch.products,
        },
      ];
      state.productSearch = productSearch;
      state.caches.categoriesByUrl[productSearch.category.url] =
        productSearch.category;
    },
    /**
     * Add a filter to filters
     * @param {ProductListState} state - state of the module
     * @param {ProductSearchRefinementValue} refinementValue - selected filter
     */
    selectFilter(state, refinementValue) {
      if (isRefinementValueForPrice(refinementValue)) {
        // Remove any previous price filter to avoid adding more than one range
        removeAnyPriceFilterFromState(state);
      }
      state.ui.selectedFilters.push(refinementValue);
    },
    /**
     * Remove a filter from filters
     * @param {ProductListState} state - state of the module
     * @param {ProductSearchRefinementValue} refinementValue - selected filter
     */
    deselectFilter(state, refinementValue) {
      state.ui.selectedFilters = state.ui.selectedFilters.filter(
        (selectedFilter) => selectedFilter.value !== refinementValue.value
      );
    },
    /**
     * Remove any applied price filter
     * @param {ProductListState} state - state of the module
     */
    deselectPriceFilter: removeAnyPriceFilterFromState,
    /**
     * Set sort to the given option
     * @param {ProductListState} state - state of the module
     * @param {SortingOption} sortingOption - selected sorting option
     */
    selectSortingOption(state, sortingOption) {
      state.ui.selectedSortingOption = sortingOption;
    },
    /**
     * Set PLP column mode (num cols)
     * @param {ProductListState} state - state of the module
     * @param {string} columnMode - column mode to apply
     */
    setColumnMode(state, columnMode) {
      state.ui.columnMode = columnMode;
    },
    /**
     * Set the category the user is currently looking at within the list
     * @param {ProductListState} state - state of the module
     * @param {Object} category - category data
     */
    setVisibleCategory(state, category) {
      state.visibleCategory = category;
    },
    /**
     * Record which category is being displayed as its own view all. A view all
     * shares its url with its category, so this is the only thing telling both
     * views apart.
     * @param {ProductListState} state - state of the module
     * @param {?string} categoryId - category id, or null to leave the view all
     */
    setViewAllCategoryId(state, categoryId) {
      state.viewAllCategoryId = categoryId ?? null;
    },
  },
  actions: {
    /**
     * Set up the list with the data rendered server side
     * @param {Object} context - Vuex action context
     * @param {Object} payload - initial data
     * @param {ProductSearch} payload.productSearch - search results
     * @param {string} payload.url - URL the results come from
     * @returns {void}
     */
    init({ commit, dispatch }, { productSearch, url }) {
      productSearchPrefetcher = createProductSearchPrefetcher(productSearch);
      dispatch('resetList', productSearch);
      commit('resetFilters', productSearch);
      commit('loaded', parseRelativeUrl(url));
      commit('loaded', parseRelativeUrl(productSearch.permalink));
    },
    /**
     * Append or prepend a further page of results, skipping URLs already loaded
     * @param {Object} context - Vuex action context
     * @param {Object} payload - page to load
     * @param {string} payload.url - URL of the page
     * @param {number} [payload.minLoadingTime=0] - ms to keep the spinner up,
     * used when the page belongs to a different category
     * @param {boolean} [payload.prefetchAdjacent=true] - prefetch the next page
     * @returns {Promise<string>} resolves with the loaded URL
     */
    loadPageFromUrl(
      { commit, state },
      { url, minLoadingTime = 0, prefetchAdjacent = true }
    ) {
      if (state.loadedUrls.indexOf(url) > -1) {
        return Promise.resolve(url);
      }
      commit('loading');

      const pagePromise =
        Number.isInteger(minLoadingTime) && minLoadingTime > 0
          ? pMinDelay(productSearchPrefetcher.get(url), minLoadingTime)
          : productSearchPrefetcher.get(url, prefetchAdjacent);

      return pagePromise.then((productSearch) => {
        mutateCategory({ commit, state }, productSearch);
        commit('loaded', url);
        return url;
      });
    },
    /**
     * Swap a tile to one of its color variations
     * @param {Object} productListModule - Vuex action context
     * @param {Object} payload - variation to load
     * @param {Object} payload.color - color to switch the tile to
     * @param {string} payload.type - tile type
     * @param {number} payload.index - position of the tile in the list
     * @param {boolean} payload.expand - whether the tile is expanded
     * @returns {void}
     */
    loadColorVariation(productListModule, { color, type, index, expand }) {
      loadColorVariation(productListModule, {
        color,
        type,
        index,
        expand,
      });
    },
    /**
     * Lazy-load the full tile data for a slider banner's products (used on
     * desktop, where each slider product renders as a normal grid tile). The
     * full products are stored on `banner.loadedProducts`, leaving the
     * lightweight `banner.productSlider` untouched for mobile. We mutate the
     * exact `banner` object the grid renders (the one spliced into the list by
     * addBannersToProductSearchResult), so no index lookup is needed. Idempotent:
     * skips if already loaded or in flight.
     * @param {Object} context Vuex action context
     * @param {{banner: Object}} payload the banner whose slider products to load
     * @returns {Promise} resolves once loaded (or immediately if already loaded)
     */
    loadSliderBannerProducts({ commit }, { banner }) {
      const key = getSliderBannerKey(banner);
      if (
        !banner?.productSlider ||
        isSliderBannerEnriched(banner) ||
        pendingSliderBanners.has(key)
      ) {
        return Promise.resolve();
      }
      pendingSliderBanners.add(key);
      return Axios.get(FRONT_CONFIG.urls.getProductInfoList, {
        // fullImages=true -> Product-ProductTileJson with plainimages=false, so
        // the tiles come with the product's PLP images (not plain), which the
        // desktop grid renders. The banner key is the comma-separated ids.
        params: { ids: key, fullImages: true },
      })
        .then(({ data }) => {
          const products = (data.products || [])
            .filter((entry) => entry && entry.product)
            .map((entry) => {
              const product = { ...entry.product };
              if (!product.selectedProductUrl && entry.urls?.product) {
                product.selectedProductUrl = entry.urls.product;
              }
              return product;
            });
          if (products.length) {
            commit('enrichSliderBannerProducts', { banner, products });
          } else {
            // Resolved but no usable products (e.g. all offline) -> hide.
            commit('failSliderBannerProducts', { banner });
          }
        })
        .catch(() => {
          commit('failSliderBannerProducts', { banner });
        })
        .finally(() => {
          pendingSliderBanners.delete(key);
        });
    },
    /**
     * Reset the list and load a new one filtered with data from a new URL
     *
     * @param {Object} context - Vuex action context
     * @param {string} url - URL to load a new product search
     * @returns {Promise<string>} resolves with the loaded URL
     */
    loadFilteredListFromUrl({ commit, dispatch, state }, url) {
      if (state.filtersUrl === url) {
        return Promise.resolve(url);
      }
      commit('loading', true);
      productSearchPrefetcher = createProductSearchPrefetcher();
      return productSearchPrefetcher.get(url).then((productSearch) => {
        dispatch('resetList', productSearch);
        commit('loaded', urlWithoutParams(url));
        commit('loaded', url);
        commit('filtered', url);
        return url;
      });
    },
    /**
     * Reset the list and load a new one with data from a new URL
     *
     * @param {Object} context - Vuex action context
     * @param {ProductListState} context.state - current state
     * @param {string} url - URL to load a new product search
     * @returns {Promise<string>} resolves with the loaded URL
     */
    loadNewListFromUrl({ commit, dispatch, state }, url) {
      if (state.caches.categoriesByUrl[url]) {
        /* Set visible category if it is cached. Useful temporary patch for
        refreshing breadcrumbs when navigating between categories from menu
        without reloading. Only suitable for categories loaded in a long PLP. */
        /* TODO: Remove this patch after improving the observers that detect
        the visible category */
        commit('setVisibleCategory', state.caches.categoriesByUrl[url]);
        dispatch('categories/setCategory', state.caches.categoriesByUrl[url], {
          root: true,
        });
      }
      if (state.loadedUrls.indexOf(url) > -1) {
        return Promise.resolve(url);
      }
      commit('loading', true);
      // Reset list before loading new category initial page
      productSearchPrefetcher = createProductSearchPrefetcher();
      commit('resetFilters');
      return productSearchPrefetcher.get(url).then((productSearch) => {
        dispatch('resetList', productSearch);
        commit('resetFilters', productSearch);
        commit('loaded', urlWithoutParams(url));
        commit('loaded', url);
        return url;
      });
    },
    /**
     * Start the list over with a single category and publish it as the current
     * one for the rest of the app
     * @param {Object} context - Vuex action context
     * @param {ProductSearch} productSearch - search results to start over with
     * @returns {void}
     */
    resetList: ({ commit, dispatch }, productSearch) => {
      commit('resetStateList', productSearch);
      dispatch('categories/setCategory', productSearch.category, {
        root: true,
      });
    },
  },
  getters: {
    /**
     * Retrieve the number of applied filters and sorting in current productSearch
     * Skip category filters
     * @param {ProductListState} state - state of the module
     * @returns {number} applied filters, plus one when a sorting is applied
     */
    numAppliedFilters: (state) => {
      const applicableFilters = filtersWithoutCategory(
        state.productSearch?.selectedFilters
      );
      const numFilters = applicableFilters?.length;
      const hasSorting = hasAnySortingOptionApplied(state.productSearch);
      let result = numFilters;
      if (hasSorting) {
        result = numFilters + 1;
      }
      return result;
    },
    /**
     * Retrieve the number of ui applied filters and sorting in current productSearch
     * Skip category filters
     * @param {ProductListState} state - state of the module
     * @returns {number} applied filters in ui, plus one when a sorting is applied
     */
    uiNumAppliedFilters: (state) => {
      let count = state.ui.appliedFilters.length;
      if (state.ui.appliedSortingOption !== null) {
        count += 1;
      }
      return count;
    },
    /**
     * Retrieve the number of ui selected filters and sorting in current productSearch
     * even if they are not applied yet
     * Skip category filters
     * @param {ProductListState} state - state of the module
     * @returns {number} selected filters in ui, plus one when a sorting is selected
     */
    uiNumSelectedFilters: (state) => {
      let count = state.ui.selectedFilters.length;
      if (state.ui.selectedSortingOption !== null) {
        count += 1;
      }
      return count;
    },
    /**
     * Retrieve the product list, with a separator inserted between the tiles of
     * each pair of consecutive categories
     * @param {ProductListState} state - state of the module
     * @returns {Object[]} tiles to render, separators included
     */
    products: (state) =>
      state.products.flatMap((categoryResults, index) =>
        index > 0
          ? [
              createCategorySeparator(
                categoryResults.category, // current category
                state.products[index - 1].category // previous category
              ),
              ...categoryResults.items,
            ] // Add linebreaks between categories
          : categoryResults.items
      ),
    /**
     * Category the user is looking at, only while the list is visible
     * @param {ProductListState} state - state of the module
     * @returns {?Object} visible category data, null when the list is hidden
     */
    visibleCategory: (state) =>
      !state.ui.hidden ? state.visibleCategory : null,
    /**
     * Category the user is looking at, whether the list is visible or not
     * @param {ProductListState} state - state of the module
     * @returns {?Object} current category data
     */
    currentCategory: (state) => state.visibleCategory,
    /**
     * Id of the category being displayed as its own view all
     * @param {ProductListState} state - state of the module
     * @returns {?string} category id, null while displaying the category itself
     */
    viewAllCategoryId: (state) => state.viewAllCategoryId,
  },
};
